import {
  Component,
  DestroyRef,
  inject,
  input,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { ModalService } from '@features/modals/services/modal.service';
import { CoinflipDeprecatedService } from '@old-app/coinflip/coinflip.service';
import { CoinflipSkinItem, CoinflipWon } from '@old-app/coinflip/interfaces/coinflip.interface';
import { CoinflipCoin } from '@features/coinflip/types/coins.type';
import { CostComponent } from '@core/components/cost/cost.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { NgParticlesService, NgxParticlesModule } from '@tsparticles/angular';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { SkinsListComponent } from '@core/components/skins-list/skins-list.component';
import { CheckboxComponent } from '@core/components/checkbox/checkbox.component';
import { Skin } from '@core/interfaces/skin.interface';
import 'number-flow';

@Component({
  selector: 'app-coinflip-won-modal-new',
  templateUrl: './coinflip-won-modal-new.component.html',
  styleUrls: ['./coinflip-won-modal-new.component.scss'],
  standalone: true,
  imports: [
    CostComponent,
    SvgIconComponent,
    NgxParticlesModule,
    SkinsListComponent,
    CheckboxComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'modal-container flex flex-col',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoinflipWonModalNewComponent implements OnInit {
  public modalService = inject(ModalService);
  private coinflipService = inject(CoinflipDeprecatedService);
  private ngParticlesService = inject(NgParticlesService);
  private destroyRef = inject(DestroyRef);

  // Inputs
  readonly coinflipGame = input.required<CoinflipWon>();
  readonly skins = input.required<CoinflipSkinItem[]>();
  readonly coinflipWonCoin = input.required<CoinflipCoin>();

  // State
  readonly selectedSkins = signal<Skin[]>([]);
  readonly countdownDuration = signal(30);
  readonly selectAll = signal(true);
  private timerInterval: any;

  // Computed
  readonly selectedCount = computed(() => this.selectedSkins().length);
  readonly totalCount = computed(() => this.skins().length);

  readonly selectedValue = computed(() =>
    this.selectedSkins().reduce((acc, skin) => acc + skin.price, 0)
  );

  readonly totalValue = computed(() =>
    this.skins().reduce((acc, skin) => acc + skin.price, 0)
  );

  readonly winnerChance = computed(() => {
    const game = this.coinflipGame();
    return game.winner?.chance ?? 50;
  });

  readonly winnerAvatar = computed(() => {
    const game = this.coinflipGame();
    return game.winner?.avatar ?? '';
  });

  // Particles config
  particlesOptions: ISourceOptions = {
    fullScreen: { enable: false },
    particles: {
      number: { value: 30 },
      color: { value: ['#FFD700', '#FFA500', '#ffffff'] },
      move: {
        direction: 'none',
        enable: true,
        outModes: { default: 'out' },
        random: true,
        speed: 1,
        straight: false,
      },
      opacity: {
        value: { min: 0.17, max: 0.5 },
      },
      size: {
        value: { min: 1, max: 1.2 },
      },
    },
  };

  ngOnInit() {
    // Initialize particles
    this.ngParticlesService.init(async (engine: Engine) => {
      await loadSlim(engine);
    });

    // Select all skins by default
    this.selectedSkins.set([...this.skins()]);

    // Start countdown timer
    this.timerInterval = setInterval(() => {
      const newDuration = this.countdownDuration() - 1;
      this.countdownDuration.set(newDuration);

      if (newDuration < 0) {
        this.modalService.close();
      }
    }, 1000);

    this.destroyRef.onDestroy(() => clearInterval(this.timerInterval));
  }

  onSelectAllChange(checked: boolean) {
    this.selectAll.set(checked);
    if (checked) {
      this.selectedSkins.set([...this.skins()]);
    } else {
      this.selectedSkins.set([]);
    }
  }

  async instantDeposit() {
    // Deposit selected skins back to coinflip
    // TODO: Implement actual deposit logic
    this.modalService.close();
  }

  async withdrawEverything() {
    await this.coinflipService.claimWinnings(this.coinflipGame().gameid);
    this.modalService.close();
  }
}
